import { type MenuElement, MenuItem } from "prosemirror-menu";
import { type EditorState, NodeSelection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { isColorMark, schema } from "../adapter/schema";
import { ICON_SIZE } from "../icons";
import { FORMULA_ERROR_COLOR } from "../node-views/formula";
import { toolbarPluginKey } from "../plugins/toolbar";
import {
  getSelectionColor,
  isFormulaErrorSelected,
  isImageSelected,
  isSelectionInCodeBlock,
} from "../utils";

import { COLOR_PALETTE } from "./text-color-palette";

import { t } from "i18n/i18n";
import { TEXT_COLOR_ICON_PATHS } from "mds/utils/icon-paths";

const DEFAULT_TEXT_COLOR = "#000000";
const DEFAULT_CODE_BLOCK_TEXT_COLOR = "#FFFFFF";
const SVG_NS = "http://www.w3.org/2000/svg";

/** Returns the active text color for the icon indicator; defaults to black or white depending on context. */
function getDisplayedTextColor(view: EditorView): string {
  const { state } = view;
  const defaultColor = isSelectionInCodeBlock(state)
    ? DEFAULT_CODE_BLOCK_TEXT_COLOR
    : DEFAULT_TEXT_COLOR;

  if (isFormulaErrorSelected(state)) return FORMULA_ERROR_COLOR;

  const colorMarkType = schema.marks.color;
  if (!colorMarkType) return defaultColor;

  const sel = state.selection;

  if (sel instanceof NodeSelection) {
    const colorMark = sel.node.marks.find(isColorMark);
    return colorMark ? colorMark.attrs.color : defaultColor;
  }

  // Empty selection (cursor): use storedMarks or $from.marks() to reflect what the next typed character will look like.
  if (sel.empty) {
    const marks = state.storedMarks || sel.$from.marks();
    const colorMark = marks.find(isColorMark);
    return colorMark ? colorMark.attrs.color : defaultColor;
  }

  return getSelectionColor(state, sel.from, sel.to) ?? defaultColor;
}

/** Creates a toolbar button that opens the text color panel. */
export function textColorPanelItem(): MenuElement {
  return {
    render(view: EditorView) {
      const btn = document.createElement("button");
      btn.className = "flex items-center justify-center";
      btn.title = t("worksheet.rich_text_editor.text_color");

      const svg = document.createElementNS(SVG_NS, "svg");
      svg.setAttribute("viewBox", `0 0 ${ICON_SIZE} ${ICON_SIZE}`);
      svg.setAttribute("width", String(ICON_SIZE));
      svg.setAttribute("height", String(ICON_SIZE));

      const aPath = document.createElementNS(SVG_NS, "path");
      aPath.setAttribute("d", TEXT_COLOR_ICON_PATHS[0]);
      aPath.setAttribute("fill", "currentColor");

      const barPath = document.createElementNS(SVG_NS, "path");
      barPath.setAttribute("d", TEXT_COLOR_ICON_PATHS[1]);
      barPath.setAttribute("fill", getDisplayedTextColor(view));

      svg.appendChild(aPath);
      svg.appendChild(barPath);
      btn.appendChild(svg);

      btn.addEventListener("click", (event) => {
        if (btn.classList.contains("ProseMirror-menu-disabled")) return;
        const isKeyboard = event.detail === 0; // detail = click count
        view.focus();
        view.dispatch(
          view.state.tr.setMeta(toolbarPluginKey, {
            activePanelId: "textColor",
            shouldFocus: isKeyboard,
          }),
        );
      });

      return {
        dom: btn,
        update: (state: EditorState) => {
          barPath.setAttribute("fill", getDisplayedTextColor(view));
          const disabled =
            isSelectionInCodeBlock(state) ||
            isFormulaErrorSelected(state) ||
            isImageSelected(state);
          btn.classList.toggle("ProseMirror-menu-disabled", disabled);
          return true;
        },
      };
    },
  };
}

/** Returns all text color MenuItems for the text color panel. */
export function textColorItems(): MenuElement[] {
  return COLOR_PALETTE.map((color) => {
    return new MenuItem({
      label: "",
      render() {
        const btn = document.createElement("button");
        btn.className = "text-color-panel-item transition-transform duration-100 hover:scale-110";
        btn.setAttribute("data-color", color);
        btn.style.backgroundColor = color;
        return btn;
      },
      run(state, dispatch, view) {
        const colorMarkType = schema.marks.color;
        if (!colorMarkType) return false;
        const { from, to, empty } = state.selection;
        const mark = colorMarkType.create({ color });
        const tr = empty ? state.tr.addStoredMark(mark) : state.tr.addMark(from, to, mark);
        dispatch?.(tr.setMeta(toolbarPluginKey, { activePanelId: "main" }));
        view?.focus();
        return true;
      },
    });
  });
}
